'use client';
import { useEffect, useRef } from 'react';

/**
 * Sign the user out after a stretch of inactivity. Defends shared
 * devices — a teacher signed in at a studio iPad walks away without
 * locking it, an admin leaves their laptop open at a cafe, etc.
 *
 * Activity = pointer, key, touch, or visibility-change events.
 * Detection is throttled (we only update the timestamp once per
 * minute) so high-frequency mousemove doesn't pin the CPU.
 *
 * Cross-tab coordination uses localStorage: any active tab refreshes
 * the shared "last-active-at" so being active in one tab keeps all
 * tabs alive. Sign-out fires from whichever tab first crosses the
 * threshold.
 */

const STORAGE_KEY = 'cfva.last-active-at';
const ACTIVITY_THROTTLE_MS = 60_000; // record at most once per minute
const CHECK_INTERVAL_MS = 60_000; // poll the threshold once per minute

export function useIdleSignOut(opts: {
  enabled: boolean;
  idleMs: number; // sign out after this much inactivity
  onTimeout: () => void; // typically auth.signOut()
}) {
  const lastWrittenRef = useRef(0);

  useEffect(() => {
    if (!opts.enabled) return;

    const now = Date.now();
    const stored = Number(localStorage.getItem(STORAGE_KEY) || 0);
    // If the stored value is already older than idleMs, the user was
    // idle while away — sign out immediately on this tab's mount.
    if (stored && now - stored > opts.idleMs) {
      opts.onTimeout();
      return;
    }
    if (!stored) {
      localStorage.setItem(STORAGE_KEY, String(now));
      lastWrittenRef.current = now;
    }

    const recordActivity = () => {
      const t = Date.now();
      if (t - lastWrittenRef.current < ACTIVITY_THROTTLE_MS) return;
      lastWrittenRef.current = t;
      localStorage.setItem(STORAGE_KEY, String(t));
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach((e) => window.addEventListener(e, recordActivity, { passive: true }));
    // visibilitychange — coming back from a tab/window switch counts as activity.
    const onVis = () => {
      if (document.visibilityState === 'visible') recordActivity();
    };
    document.addEventListener('visibilitychange', onVis);

    const interval = setInterval(() => {
      const last = Number(localStorage.getItem(STORAGE_KEY) || 0);
      if (last && Date.now() - last > opts.idleMs) {
        opts.onTimeout();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      events.forEach((e) => window.removeEventListener(e, recordActivity));
      document.removeEventListener('visibilitychange', onVis);
      clearInterval(interval);
    };
    // Deps intentionally granular: depending on the whole `opts` object would
    // re-attach all listeners on every parent render because the caller
    // typically passes a fresh object literal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.enabled, opts.idleMs, opts.onTimeout]);
}

/**
 * Clear the shared last-active-at marker. Call from signOut so a
 * subsequent sign-in starts fresh.
 */
export function resetIdleTracker() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* SSR / disabled */
  }
}
